import apiClient from './index';
import {
  HeatCalculationRunResponse,
  HeatCalculationSessionCreate,
  HeatCalculationSessionAttachImport,
  HeatCalculationSessionList,
  HeatCalculationSessionRead,
  HeatCalculationSessionUpdate,
  HeatHousePeriodBase,
  HeatHousePeriodRead,
  HeatPeriodReadingsActionResult,
  HeatRadiatorCatalogList,
  HeatRadiatorCatalogPayload,
  HeatRadiatorCatalogRead,
  HeatReadinessResponse,
  HeatRadiatorPayload,
  HeatRadiatorRead,
  HeatRadiatorRevisionPayload,
  HeatRadiatorRevisionRead,
  HeatReportResponse,
} from './types';

export const heatApi = {
  async getRadiator(deviceId: string): Promise<HeatRadiatorRead> {
    const res = await apiClient.get<HeatRadiatorRead>(`/heat/devices/${deviceId}/radiator`);
    return res.data;
  },

  async upsertRadiator(deviceId: string, payload: HeatRadiatorPayload): Promise<HeatRadiatorRead> {
    const res = await apiClient.put<HeatRadiatorRead>(`/heat/devices/${deviceId}/radiator`, payload);
    return res.data;
  },

  async deleteRadiator(deviceId: string): Promise<void> {
    await apiClient.delete(`/heat/devices/${deviceId}/radiator`);
  },

  async listRadiatorRevisions(deviceId: string): Promise<HeatRadiatorRevisionRead[]> {
    const res = await apiClient.get<HeatRadiatorRevisionRead[]>(`/heat/devices/${deviceId}/radiator/revisions`);
    return res.data;
  },

  async replaceRadiator(deviceId: string, payload: HeatRadiatorRevisionPayload): Promise<HeatRadiatorRevisionRead> {
    const res = await apiClient.post<HeatRadiatorRevisionRead>(`/heat/devices/${deviceId}/radiator/revisions`, payload);
    return res.data;
  },

  async listCatalog(query?: string): Promise<HeatRadiatorCatalogList> {
    const res = await apiClient.get<HeatRadiatorCatalogList>('/heat/catalog', {
      params: query ? { q: query } : undefined,
    });
    return res.data;
  },

  async createCatalogItem(payload: HeatRadiatorCatalogPayload): Promise<HeatRadiatorCatalogRead> {
    const res = await apiClient.post<HeatRadiatorCatalogRead>('/heat/catalog', payload);
    return res.data;
  },

  async updateCatalogItem(itemId: string, payload: Partial<HeatRadiatorCatalogPayload>): Promise<HeatRadiatorCatalogRead> {
    const res = await apiClient.patch<HeatRadiatorCatalogRead>(`/heat/catalog/${itemId}`, payload);
    return res.data;
  },

  async deleteCatalogItem(itemId: string): Promise<void> {
    await apiClient.delete(`/heat/catalog/${itemId}`);
  },

  async listPeriods(buildingId: string): Promise<HeatHousePeriodRead[]> {
    const res = await apiClient.get<HeatHousePeriodRead[]>(`/heat/buildings/${buildingId}/periods`);
    return res.data;
  },

  async createPeriod(buildingId: string, payload: HeatHousePeriodBase): Promise<HeatHousePeriodRead> {
    const res = await apiClient.post<HeatHousePeriodRead>(`/heat/buildings/${buildingId}/periods`, payload);
    return res.data;
  },

  async updatePeriod(periodId: string, payload: Partial<HeatHousePeriodBase>): Promise<HeatHousePeriodRead> {
    const res = await apiClient.patch<HeatHousePeriodRead>(`/heat/periods/${periodId}`, payload);
    return res.data;
  },

  async deletePeriod(periodId: string): Promise<void> {
    await apiClient.delete(`/heat/periods/${periodId}`);
  },

  async setPeriodLock(periodId: string, locked: boolean): Promise<HeatHousePeriodRead> {
    const res = await apiClient.post<HeatHousePeriodRead>(`/heat/periods/${periodId}/lock`, { locked });
    return res.data;
  },

  async copyPreviousReadings(periodId: string): Promise<HeatPeriodReadingsActionResult> {
    const res = await apiClient.post<HeatPeriodReadingsActionResult>(
      `/heat/periods/${periodId}/copy-previous-readings`
    );
    return res.data;
  },

  async deletePeriodReadings(periodId: string): Promise<HeatPeriodReadingsActionResult> {
    const res = await apiClient.delete<HeatPeriodReadingsActionResult>(
      `/heat/periods/${periodId}/readings`
    );
    return res.data;
  },

  async buildReport(periodId: string, options?: { allowMissingCoefficientsArea?: boolean }): Promise<HeatReportResponse> {
    const res = await apiClient.get<HeatReportResponse>(`/heat/periods/${periodId}/report`, {
      params: options?.allowMissingCoefficientsArea
        ? { allow_missing_coefficients_area: true }
        : undefined,
    });
    return res.data;
  },

  async downloadReportExcel(
    periodId: string,
    format: 'short' | 'full' = 'full',
    options?: { allowMissingCoefficientsArea?: boolean },
  ): Promise<Blob> {
    const res = await apiClient.get(`/heat/periods/${periodId}/report.xlsx`, {
      params: {
        format,
        ...(options?.allowMissingCoefficientsArea
          ? { allow_missing_coefficients_area: true }
          : {}),
      },
      responseType: 'blob',
    });
    return res.data;
  },

  async downloadReportC300(
    periodId: string,
    options?: { allowMissingCoefficientsArea?: boolean },
  ): Promise<Blob> {
    const res = await apiClient.get(`/heat/periods/${periodId}/report.c300.csv`, {
      params: options?.allowMissingCoefficientsArea
        ? { allow_missing_coefficients_area: true }
        : undefined,
      responseType: 'blob',
    });
    return res.data;
  },

  async downloadReceiptsZip(
    periodId: string,
    options?: { allowMissingCoefficientsArea?: boolean },
  ): Promise<Blob> {
    const res = await apiClient.get(`/heat/periods/${periodId}/receipts.zip`, {
      params: options?.allowMissingCoefficientsArea
        ? { allow_missing_coefficients_area: true }
        : undefined,
      responseType: 'blob',
    });
    return res.data;
  },

  async downloadApartmentReceipt(
    periodId: string,
    apartmentId: string,
    format: 'xlsx' | 'pdf' | 'zip' = 'zip',
    options?: { allowMissingCoefficientsArea?: boolean },
  ): Promise<Blob> {
    const res = await apiClient.get(
      `/heat/periods/${periodId}/apartments/${apartmentId}/receipt.${format}`,
      {
        params: options?.allowMissingCoefficientsArea
          ? { allow_missing_coefficients_area: true }
          : undefined,
        responseType: 'blob',
      },
    );
    return res.data;
  },

  async listSessions(buildingId: string): Promise<HeatCalculationSessionList> {
    const res = await apiClient.get<HeatCalculationSessionList>(`/heat/buildings/${buildingId}/sessions`);
    return res.data;
  },

  async createSession(buildingId: string, payload: HeatCalculationSessionCreate): Promise<HeatCalculationSessionRead> {
    const res = await apiClient.post<HeatCalculationSessionRead>(`/heat/buildings/${buildingId}/sessions`, payload);
    return res.data;
  },

  async updateSession(sessionId: string, payload: HeatCalculationSessionUpdate): Promise<HeatCalculationSessionRead> {
    const res = await apiClient.patch<HeatCalculationSessionRead>(`/heat/sessions/${sessionId}`, payload);
    return res.data;
  },

  async attachImport(sessionId: string, payload: HeatCalculationSessionAttachImport): Promise<HeatCalculationSessionRead> {
    const res = await apiClient.post<HeatCalculationSessionRead>(`/heat/sessions/${sessionId}/attach-import`, payload);
    return res.data;
  },

  async calculateSession(sessionId: string, options?: { allowMissingCoefficientsArea?: boolean }): Promise<HeatCalculationRunResponse> {
    const res = await apiClient.post<HeatCalculationRunResponse>(
      `/heat/sessions/${sessionId}/calculate`,
      null,
      {
        params: options?.allowMissingCoefficientsArea
          ? { allow_missing_coefficients_area: true }
          : undefined,
      },
    );
    return res.data;
  },

  async getSessionReport(
    sessionId: string,
    options?: { allowMissingCoefficientsArea?: boolean; recalculate?: boolean },
  ): Promise<HeatReportResponse> {
    const params: Record<string, boolean> = {};
    if (options?.allowMissingCoefficientsArea) {
      params.allow_missing_coefficients_area = true;
    }
    if (options?.recalculate) {
      params.recalculate = true;
    }
    const res = await apiClient.get<HeatReportResponse>(`/heat/sessions/${sessionId}/report`, {
      params: Object.keys(params).length ? params : undefined,
    });
    return res.data;
  },

  async closeSession(sessionId: string): Promise<HeatCalculationSessionRead> {
    const res = await apiClient.post<HeatCalculationSessionRead>(`/heat/sessions/${sessionId}/close`);
    return res.data;
  },

  async getReadiness(buildingId: string, periodId?: string | null): Promise<HeatReadinessResponse> {
    const res = await apiClient.get<HeatReadinessResponse>(`/heat/buildings/${buildingId}/readiness`, {
      params: periodId ? { period_id: periodId } : undefined,
    });
    return res.data;
  },
};
